import { API_BASE, getAdminToken, setAdminToken } from "./api";

const SESSION_KEY = "ga_cognito_session";
const AUTH_MODE_KEY = "ga_auth_mode";

export type AuthMode = "cognito" | "token";

export interface CognitoPublicConfig {
  cognitoEnabled: boolean;
  cognitoRegion: string;
  cognitoUserPoolId: string | null;
  cognitoClientId: string | null;
}

export interface CognitoSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  email: string;
}

export interface MeResponse {
  email: string;
  role: "super-admin" | "admin" | "user";
  source: "cognito" | "api_token" | "dev";
  groups: string[];
}

interface CognitoAuthResult {
  AuthenticationResult?: {
    IdToken?: string;
    AccessToken?: string;
    RefreshToken?: string;
    ExpiresIn?: number;
  };
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split(".")[1];
  if (!part) return {};
  const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json) as Record<string, unknown>;
}

export function getAuthMode(): AuthMode {
  return localStorage.getItem(AUTH_MODE_KEY) === "token" ? "token" : "cognito";
}

export function setAuthMode(mode: AuthMode): void {
  localStorage.setItem(AUTH_MODE_KEY, mode);
}

export function loadSession(): CognitoSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as CognitoSession;
    if (!session.idToken) return null;
    return session;
  } catch {
    return null;
  }
}

function saveSession(session: CognitoSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  setAdminToken(session.idToken);
  setAuthMode("cognito");
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  setAdminToken("");
}

export async function fetchCognitoConfig(): Promise<CognitoPublicConfig> {
  const response = await fetch(`${API_BASE}/health`);
  if (!response.ok) throw new Error(`Could not load auth config (HTTP ${response.status})`);
  const payload = await response.json() as { auth?: CognitoPublicConfig };
  const auth = payload.auth;
  if (!auth) {
    return { cognitoEnabled: false, cognitoRegion: "ap-south-1", cognitoUserPoolId: null, cognitoClientId: null };
  }
  return auth;
}

async function cognitoCall(region: string, target: string, body: Record<string, unknown>): Promise<CognitoAuthResult> {
  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json() as CognitoAuthResult & { message?: string; __type?: string };
  if (!response.ok) {
    throw new Error(payload.message || payload.__type || `Cognito ${target} failed`);
  }
  return payload;
}

function sessionFromAuth(result: CognitoAuthResult, emailHint?: string): CognitoSession {
  const auth = result.AuthenticationResult;
  if (!auth?.IdToken || !auth.AccessToken) throw new Error("Cognito did not return tokens.");
  const claims = decodeJwtPayload(auth.IdToken);
  const email = String(claims.email || claims["cognito:username"] || emailHint || "unknown");
  const expiresIn = auth.ExpiresIn ?? 3600;
  return {
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken ?? loadSession()?.refreshToken ?? "",
    expiresAt: Date.now() + expiresIn * 1000 - 60_000,
    email,
  };
}

export async function loginWithPassword(email: string, password: string, config: CognitoPublicConfig): Promise<{
  session?: CognitoSession;
  newPasswordRequired?: { session: string; email: string };
}> {
  if (!config.cognitoClientId || !config.cognitoRegion) throw new Error("Cognito is not configured.");
  const result = await cognitoCall(config.cognitoRegion, "InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: config.cognitoClientId,
    AuthParameters: {
      USERNAME: email.trim().toLowerCase(),
      PASSWORD: password,
    },
  });

  if (result.ChallengeName === "NEW_PASSWORD_REQUIRED" && result.Session) {
    return { newPasswordRequired: { session: result.Session, email: email.trim().toLowerCase() } };
  }

  const session = sessionFromAuth(result, email);
  saveSession(session);
  return { session };
}

export async function completeNewPassword(
  email: string,
  newPassword: string,
  challengeSession: string,
  config: CognitoPublicConfig,
): Promise<CognitoSession> {
  if (!config.cognitoClientId || !config.cognitoRegion) throw new Error("Cognito is not configured.");
  const result = await cognitoCall(config.cognitoRegion, "RespondToAuthChallenge", {
    ClientId: config.cognitoClientId,
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    Session: challengeSession,
    ChallengeResponses: {
      USERNAME: email.trim().toLowerCase(),
      NEW_PASSWORD: newPassword,
    },
  });
  const session = sessionFromAuth(result, email);
  saveSession(session);
  return session;
}

export async function refreshSession(config: CognitoPublicConfig, force = false): Promise<CognitoSession | null> {
  const session = loadSession();
  if (!session) return null;
  if (!force && session.expiresAt > Date.now()) {
    setAdminToken(session.idToken);
    return session;
  }
  if (!session.refreshToken || !config.cognitoClientId || !config.cognitoRegion) {
    clearSession();
    return null;
  }
  try {
    const result = await cognitoCall(config.cognitoRegion, "InitiateAuth", {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: config.cognitoClientId,
      AuthParameters: { REFRESH_TOKEN: session.refreshToken },
    });
    const next = sessionFromAuth(result, session.email);
    if (!next.refreshToken) next.refreshToken = session.refreshToken;
    saveSession(next);
    return next;
  } catch {
    clearSession();
    return null;
  }
}

export async function refreshSessionIfNeeded(config: CognitoPublicConfig): Promise<CognitoSession | null> {
  return refreshSession(config, false);
}

export function loginWithBreakglassToken(token: string): void {
  clearSession();
  setAdminToken(token.trim());
  setAuthMode("token");
}

export function signOut(): void {
  clearSession();
  localStorage.removeItem(AUTH_MODE_KEY);
}

export function hasStoredAuth(): boolean {
  return Boolean(getAdminToken());
}
